import { type ReactNode } from 'react';

import { Reveal } from '../components/effects/Reveal';
import { Spotlight } from '../components/effects/Spotlight';
import { useHorizontalCarousel } from '../hooks/useHorizontalCarousel';

const CARD_WIDTH = 460;
const CARD_GAP = 28;

interface ServiceItem {
  num: string;
  title: ReactNode;
  short: string;
  desc: string;
  chips: string[];
  badge?: string;
  /**
   * Color de marca por servicio — campos preformados con clases Tailwind
   * completas (JIT no detecta concatenaciones de string).
   */
  accent: {
    text: string;        // text del icono, número y CTA
    bg: string;          // fondo sólido del badge "Práctica principal"
    bgSoft: string;      // fondo suave (chips, icon container)
    ring: string;        // ring del borde en hover
    sideBar: string;     // barra vertical izquierda
    iconHover: string;   // clases combinadas para invertir icon container en hover
    spotlight: string;   // rgba para Spotlight
  };
  icon: ReactNode;
}

const SERVICES: ServiceItem[] = [
  {
    num: '01',
    short: 'Consulting',
    title: 'Digital Consulting & Change Management',
    desc: 'Diseñamos hojas de ruta tecnológicas alineadas a tu estrategia de negocio y acompañamos a tu organización en el proceso de cambio.',
    chips: ['Estrategia digital', 'Gestión del cambio', 'Diagnóstico de madurez', 'Arquitecturas objetivo'],
    accent: {
      text: 'text-etribe-purple',
      bg: 'bg-etribe-purple',
      bgSoft: 'bg-etribe-purple/10',
      ring: 'group-hover:ring-etribe-purple/30',
      sideBar: 'bg-etribe-purple',
      iconHover: 'group-hover:bg-etribe-purple group-hover:text-white',
      spotlight: 'rgba(118, 58, 199, 0.10)',
    },
    icon: (
      <>
        <path d="M3 12l4-4 4 4 4-4 4 4 2-2" />
        <path d="M3 18h18" />
      </>
    ),
  },
  {
    num: '02',
    short: 'CX & CRM',
    title: (
      <>
        Customer Engagement <span className="opacity-40 font-medium">|</span> CRM{' '}
        <span className="opacity-40 font-medium">|</span> Growth
      </>
    ),
    desc: 'Convertimos cada interacción con el cliente en una oportunidad de crecimiento usando datos, CRM y experiencias personalizadas.',
    chips: ['Estrategia CX', 'Implementación CRM', 'Customer 360°', 'Growth analytics'],
    accent: {
      text: 'text-etribe-blue-light',
      bg: 'bg-etribe-blue-light',
      bgSoft: 'bg-etribe-blue-light/10',
      ring: 'group-hover:ring-etribe-blue-light/30',
      sideBar: 'bg-etribe-blue-light',
      iconHover: 'group-hover:bg-etribe-blue-light group-hover:text-white',
      spotlight: 'rgba(50, 127, 239, 0.10)',
    },
    icon: (
      <>
        <circle cx="9" cy="9" r="3" />
        <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6" />
        <circle cx="17" cy="6" r="2" />
        <path d="M15 13c2.2 0 4 1.8 4 4" />
      </>
    ),
  },
  {
    num: '03',
    short: 'Data · AI',
    title: 'Data, Analytics & AI',
    desc: 'Nuestra práctica principal. Diseñamos arquitecturas de datos modernas y aplicamos IA generativa, ML y analítica avanzada a problemas reales de negocio.',
    chips: ['#ARKEFLOW', 'Big Data', 'Data Governance', 'Data Science', 'ML & Deep Learning', 'DaaS', 'Reporting & Viz', 'Virtualización'],
    badge: 'Práctica principal',
    accent: {
      text: 'text-etribe-green',
      bg: 'bg-etribe-green',
      bgSoft: 'bg-etribe-green/10',
      ring: 'group-hover:ring-etribe-green/30',
      sideBar: 'bg-etribe-green',
      iconHover: 'group-hover:bg-etribe-green group-hover:text-white',
      spotlight: 'rgba(67, 177, 46, 0.10)',
    },
    icon: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  },
  {
    num: '04',
    short: 'Architecture',
    title: 'Technology [Innovation] Architecture',
    desc: 'Definimos arquitecturas resilientes, modernas y preparadas para escalar con IA y cargas críticas.',
    chips: ['Cloud GCP / AWS / Azure', 'Data Mesh', 'Modernización de apps', 'DevOps & FinOps'],
    accent: {
      text: 'text-etribe-orange',
      bg: 'bg-etribe-orange',
      bgSoft: 'bg-etribe-orange/10',
      ring: 'group-hover:ring-etribe-orange/30',
      sideBar: 'bg-etribe-orange',
      iconHover: 'group-hover:bg-etribe-orange group-hover:text-white',
      spotlight: 'rgba(255, 130, 4, 0.10)',
    },
    icon: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" />
      </>
    ),
  },
];

export default function Services() {
  const {
    trackRef,
    canPrev,
    canNext,
    onScroll,
    scrollByStep,
    scrollToIndex,
    isDotActive,
  } = useHorizontalCarousel({
    cardWidth: CARD_WIDTH,
    cardGap: CARD_GAP,
    resetKey: SERVICES.length,
  });

  return (
    <section id="servicios" className="py-[120px] relative overflow-hidden">
      {/* Background ambient */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none opacity-50"
        style={{
          background:
            'radial-gradient(40% 30% at 100% 0%, rgba(118,58,199,.06), transparent 60%), radial-gradient(40% 30% at 0% 100%, rgba(255,130,4,.05), transparent 60%)',
        }}
      />

      <div className="container-etribe">
        <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
          <Reveal>
            <div className="max-w-[720px]">
              <p className="eyebrow"><span>Portafolio</span></p>
              <h2 className="text-[clamp(32px,3.6vw,48px)] mb-4">Nuestros servicios</h2>
              <p className="text-[clamp(17px,1.3vw,20px)] text-etribe-ink-2 leading-[1.55]">
                {SERVICES.length} prácticas. Un solo socio para tu transformación digital.
              </p>
            </div>
          </Reveal>

          {(canPrev || canNext) && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => scrollByStep(-1)}
                disabled={!canPrev}
                aria-label="Servicio anterior"
                className="w-11 h-11 rounded-full border border-etribe-line bg-white flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => scrollByStep(1)}
                disabled={!canNext}
                aria-label="Siguiente servicio"
                className="w-11 h-11 rounded-full border border-etribe-line bg-white flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          onScroll={onScroll}
          className="services-carousel-track flex overflow-x-auto pb-6 mb-4 -mx-2 px-2"
          style={{
            gap: `${CARD_GAP}px`,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          aria-label="Lista de servicios"
        >
          {SERVICES.map((s, idx) => (
            <Reveal key={s.num} delay={(idx % 2) * 100}>
              <div className="flex-shrink-0" style={{ width: `${CARD_WIDTH}px` }}>
              <Spotlight
                className="group h-full"
                color={s.accent.spotlight}
                size={560}
              >
                <article
                  className={[
                    'relative h-full p-10 lg:p-12 rounded-[18px] bg-white border border-etribe-line overflow-hidden flex flex-col ring-1 ring-transparent transition-all duration-400',
                    'hover:-translate-y-2 hover:shadow-card-lg',
                    s.accent.ring,
                  ].join(' ')}
                  data-cursor="grow"
                >
                  {/* Barra lateral animada — sigue la altura completa */}
                  <span
                    aria-hidden
                    className={[
                      'absolute left-0 top-0 bottom-0 w-1 origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out',
                      s.accent.sideBar,
                    ].join(' ')}
                  />

                  {/* Número gigante de fondo (decorativo) */}
                  <span
                    aria-hidden
                    className={[
                      'absolute -top-6 -right-4 font-heading font-black text-[180px] leading-none select-none pointer-events-none opacity-[0.04] group-hover:opacity-[0.08] transition-opacity duration-500',
                      s.accent.text,
                    ].join(' ')}
                  >
                    {s.num}
                  </span>

                  {/* Header: eyebrow numerada + badge opcional */}
                  <header className="relative z-10 flex justify-between items-start mb-8 gap-4">
                    <div className="inline-flex items-center gap-2.5">
                      <span className={['font-heading text-[11px] font-bold tracking-[0.18em]', s.accent.text].join(' ')}>
                        {s.num}
                      </span>
                      <span className={['block w-6 h-px', s.accent.sideBar].join(' ')} />
                      <span className="font-heading text-[11px] tracking-[0.18em] uppercase text-etribe-gray-500">
                        {s.short}
                      </span>
                    </div>
                    {s.badge && (
                      <span className={[
                        'inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.14em] uppercase px-3 py-1.5 rounded-full text-white shadow-sm',
                        s.accent.bg,
                      ].join(' ')}>
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        {s.badge}
                      </span>
                    )}
                  </header>

                  {/* Icon container — invierte colores en hover */}
                  <div className="relative z-10 mb-8">
                    <div
                      className={[
                        'w-16 h-16 rounded-[16px] grid place-items-center transition-all duration-400 group-hover:rotate-3',
                        s.accent.bgSoft,
                        s.accent.text,
                        s.accent.iconHover,
                      ].join(' ')}
                    >
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        {s.icon}
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="relative z-10 text-[clamp(22px,1.8vw,26px)] mb-3 leading-[1.15]">
                    {s.title}
                  </h3>

                  {/* Description */}
                  <p className="relative z-10 text-etribe-ink-2 text-[15px] leading-[1.6] mb-7">
                    {s.desc}
                  </p>

                  {/* Chips con stagger en hover */}
                  <div className="relative z-10 flex flex-wrap gap-1.5 mb-8 mt-auto">
                    {s.chips.map((c, ci) => (
                      <span
                        key={c}
                        className={[
                          'text-xs px-3 py-1.5 rounded-full font-medium border border-transparent transition-all duration-300',
                          s.accent.bgSoft,
                          s.accent.text,
                          'group-hover:border-current',
                        ].join(' ')}
                        style={{ transitionDelay: `${ci * 30}ms` }}
                      >
                        {c}
                      </span>
                    ))}
                  </div>

                  {/* Footer divider + CTA */}
                  <div className="relative z-10 pt-6 border-t border-etribe-line flex items-center justify-between">
                    <a
                      href="#contacto"
                      className={[
                        'inline-flex items-center gap-2 text-sm font-semibold group/cta',
                        s.accent.text,
                      ].join(' ')}
                    >
                      Conoce más
                      <span className={[
                        'inline-flex w-7 h-7 rounded-full items-center justify-center transition-all duration-300 group-hover/cta:translate-x-1',
                        s.accent.bgSoft,
                      ].join(' ')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </span>
                    </a>
                    <span className="font-heading text-[10px] tracking-[0.18em] uppercase text-etribe-gray-2">
                      0{idx + 1} / 04
                    </span>
                  </div>
                </article>
              </Spotlight>
              </div>
            </Reveal>
          ))}
        </div>

        {SERVICES.length > 1 && (
          <div className="flex items-center justify-center gap-2">
            {SERVICES.map((s, i) => {
              const isActive = isDotActive(i);
              return (
                <button
                  key={s.num}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Ir al servicio ${s.short}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={`rounded-full transition-all duration-300 ${
                    isActive ? 'w-6 h-2 bg-etribe-blue' : 'w-2 h-2 bg-etribe-line hover:bg-etribe-blue/40'
                  }`}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
